import { useEffect, useMemo, useState } from "react";
import { MessageCircle, Send, Loader2, Search, AlertCircle, ExternalLink, CheckCircle2, Calendar } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  STAGES, STAGE_KEYS, GRADES_BY_STAGE, sectionsForGrade,
  buildWaUrl, SCHOOL_NAME,
} from "../lib/constants";
import {
  attendanceStudents, getAttendance, getWaTemplate,
  createWaMessage, updateWaStatus, listNewStudents,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function substitute(template, ctx) {
  return template
    .replaceAll("{اسم الطالب}", ctx.name || "")
    .replaceAll("{اسم المدرسة}", ctx.school || "")
    .replaceAll("{المرحلة}", ctx.stageLabel || "")
    .replaceAll("{الصف}", ctx.grade || "")
    .replaceAll("{الفصل}", ctx.section || "")
    .replaceAll("{التاريخ}", ctx.date || "");
}

export default function WhatsAppCompose() {
  const { user } = useAuth();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [stage, setStage] = useState("");
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [absentees, setAbsentees] = useState([]); // {id, name, national_id, guardian_phone, guardian_name}
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [template, setTemplate] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const allowedStages = user?.role === "super_admin" || user?.role === "admin" || !user?.assigned_stages?.length
    ? STAGE_KEYS
    : STAGE_KEYS.filter(s => user.assigned_stages.includes(s));
  const grades = stage ? GRADES_BY_STAGE[stage] : [];
  const sections = sectionsForGrade(grade);

  useEffect(() => { getWaTemplate().then(d => setTemplate(d.template)).catch(() => {}); }, []);

  const loadAbsentees = async () => {
    if (!date || !stage || !grade || !section) { toast.error("اختر التاريخ والمرحلة والصف والفصل"); return; }
    setLoading(true);
    try {
      const [att, allStudents] = await Promise.all([
        getAttendance(date, stage, grade, section),
        listNewStudents({ stage, grade, section }),
      ]);
      const absentIds = new Set((att.records || []).filter(r => r.status === "absent").map(r => r.student_id));
      const list = allStudents.filter(s => absentIds.has(s.id)).map(s => ({
        id: s.id, name: s.name, national_id: s.national_id || "",
        guardian_name: s.guardian_name || "", guardian_phone: s.guardian_phone || "",
      }));
      setAbsentees(list);
      setSelectedIds(new Set(list.filter(s => s.guardian_phone).map(s => s.id)));
      if (list.length === 0) toast.info("لا غياب لهذا اليوم 🎉");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحميل");
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    const eligible = absentees.filter(s => s.guardian_phone);
    if (selectedIds.size === eligible.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligible.map(s => s.id)));
  };

  const ctxFor = (s) => ({
    name: s.name, school: SCHOOL_NAME, stageLabel: STAGES[stage] || "",
    grade, section, date,
  });

  const messageFor = (s) => substitute(template, ctxFor(s));

  const sendOne = async (s) => {
    if (!s.guardian_phone) { toast.error("لا يوجد رقم ولي أمر"); return; }
    const msg = messageFor(s);
    const url = buildWaUrl(s.guardian_phone, msg);
    if (!url) { toast.error("رقم غير صالح"); return; }
    try {
      const logged = await createWaMessage({
        student_id: s.id, student_name: s.name,
        guardian_name: s.guardian_name, guardian_phone: s.guardian_phone,
        stage, grade, section, absence_date: date,
        message: msg, status: "pending",
      });
      window.open(url, "_blank");
      // Optimistically mark sent
      await updateWaStatus(logged.id, "sent").catch(() => {});
      toast.success(`تم فتح واتساب لـ ${s.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الإرسال");
    }
  };

  const sendBulk = async () => {
    const selected = absentees.filter(s => selectedIds.has(s.id) && s.guardian_phone);
    if (selected.length === 0) { toast.error("اختر طلاباً للإرسال"); return; }
    setSending(true);
    let ok = 0;
    for (const s of selected) {
      const msg = messageFor(s);
      const url = buildWaUrl(s.guardian_phone, msg);
      if (!url) continue;
      try {
        const logged = await createWaMessage({
          student_id: s.id, student_name: s.name,
          guardian_name: s.guardian_name, guardian_phone: s.guardian_phone,
          stage, grade, section, absence_date: date,
          message: msg, status: "pending",
        });
        window.open(url, "_blank");
        await updateWaStatus(logged.id, "sent").catch(() => {});
        ok++;
        // small delay to avoid popup blocker
        await new Promise(r => setTimeout(r, 600));
      } catch { /* skip */ }
    }
    setSending(false);
    toast.success(`تم فتح ${ok} نوافذ واتساب — اضغط "إرسال" داخل واتساب لكل رسالة`);
  };

  const selectedCount = useMemo(() => absentees.filter(s => selectedIds.has(s.id)).length, [absentees, selectedIds]);
  const missingPhone = absentees.filter(s => !s.guardian_phone).length;

  return (
    <div>
      <PageHeader title="رسائل واتساب لأولياء الأمور" subtitle="إرسال إشعار غياب لولي الأمر مع متغيرات قابلة للتعديل" icon={MessageCircle} />

      {/* Filters */}
      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Calendar size={13} /> التاريخ</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10" data-testid="wa-date" />
          </div>
          <div className="space-y-1.5">
            <Label>المرحلة</Label>
            <Select value={stage} onValueChange={(v) => { setStage(v); setGrade(""); setSection(""); }}>
              <SelectTrigger className="h-10" dir="rtl" data-testid="wa-stage"><SelectValue placeholder="اختر" /></SelectTrigger>
              <SelectContent dir="rtl">{allowedStages.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الصف</Label>
            <Select value={grade} onValueChange={(v) => { setGrade(v); setSection(""); }} disabled={!stage}>
              <SelectTrigger className="h-10" dir="rtl" data-testid="wa-grade"><SelectValue placeholder="..." /></SelectTrigger>
              <SelectContent dir="rtl">{grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الفصل</Label>
            <Select value={section} onValueChange={setSection} disabled={!grade}>
              <SelectTrigger className="h-10" dir="rtl" data-testid="wa-section"><SelectValue placeholder="..." /></SelectTrigger>
              <SelectContent dir="rtl">{sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={loadAbsentees} disabled={loading} className="h-10 w-full bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate" data-testid="btn-load-absentees">
              {loading ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />}
              تحميل الغائبين
            </Button>
          </div>
        </div>
      </Card>

      {/* Template editor */}
      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        <Label className="font-cairo font-bold text-slate-900 mb-2 block">نص الرسالة (المتغيرات يتم استبدالها تلقائياً)</Label>
        <Textarea value={template} onChange={(e) => setTemplate(e.target.value)} className="min-h-[140px] bg-white text-sm leading-relaxed" dir="rtl" data-testid="wa-template" />
        <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
          {["{اسم الطالب}", "{اسم المدرسة}", "{المرحلة}", "{الصف}", "{الفصل}", "{التاريخ}"].map(v => (
            <button key={v} type="button" onClick={() => setTemplate(t => t + " " + v)} className="px-2 py-1 rounded-md bg-teal-50 text-teal-700 hover:bg-teal-100 font-cairo">
              {v}
            </button>
          ))}
        </div>
      </Card>

      {/* Absentees list */}
      {absentees.length > 0 && (
        <Card className="border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50/60 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
              <Checkbox checked={selectedCount > 0 && selectedCount === absentees.filter(s => s.guardian_phone).length} onCheckedChange={toggleAll} data-testid="wa-toggle-all" />
              <span className="text-sm font-cairo font-bold text-slate-700">{absentees.length} غائب — اختر {selectedCount}</span>
              {missingPhone > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-[11px]"><AlertCircle size={12} /> {missingPhone} بدون رقم ولي أمر</span>
              )}
            </div>
            <Button onClick={sendBulk} disabled={sending || selectedCount === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 btn-elevate" data-testid="btn-send-bulk">
              {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
              إرسال للمحددين ({selectedCount})
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {absentees.map(s => {
              const has = !!s.guardian_phone;
              const checked = selectedIds.has(s.id);
              return (
                <div key={s.id} className={`p-3 sm:p-4 flex flex-wrap items-center gap-3 ${has ? "" : "bg-amber-50/50"}`} data-testid={`wa-row-${s.id}`}>
                  <Checkbox checked={checked} disabled={!has} onCheckedChange={() => {
                    setSelectedIds(prev => {
                      const next = new Set(prev);
                      if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                      return next;
                    });
                  }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{s.name}</div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-0.5">
                      {s.guardian_name && <span>ولي الأمر: {s.guardian_name}</span>}
                      <span dir="ltr" className="tabular-nums">{s.guardian_phone || "— لا يوجد رقم —"}</span>
                    </div>
                  </div>
                  <Button onClick={() => sendOne(s)} disabled={!has} className={`h-9 gap-2 ${has ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-300"} text-white`} data-testid={`btn-send-${s.id}`}>
                    <ExternalLink size={14} /> فتح واتساب
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="p-4 border-teal-200 bg-teal-50/40 mt-5">
        <div className="flex items-start gap-3 text-sm text-teal-800">
          <CheckCircle2 className="text-teal-600 shrink-0" size={20} />
          <div>
            <div className="font-cairo font-bold mb-1">آلية الإرسال (مجانية)</div>
            <p className="leading-relaxed text-teal-700">
              عند الضغط على إرسال، تفتح نافذة واتساب على جوازك أو متصفحك مع الرسالة جاهزة — يكفي الضغط على زر "إرسال" داخل واتساب. النظام يسجل تلقائياً جميع المحاولات في سجل الرسائل.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
